import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useEngineAliveDebounced } from "@/lib/useEngineAlive";

/**
 * EngineBadge — top-bar engine health pill, shared between AppShell
 * (tenant) and PlatformShell (platform admin).
 *
 * Phase 6ak (Hotfix Z.4) — debounce DOWN flicker. At 5s poll cadence,
 * 3 consecutive misses ≈ 15s of confirmed silence before flipping to
 * "Engine DOWN". Positive (alive=true) is always trusted immediately.
 *
 * Phase 6an (Hotfix Z.7) — the badge is now clickable whenever its
 * state isn't "up". A single click opens an inline diagnostic panel
 * showing the actual probe target, attempt timings, env vars, bound
 * listen ports, and a remediation hint. This eliminates the SSH-curl
 * loop when "Engine Checking…" persists post-upgrade.
 *
 * The debug panel only renders for users who can actually see the
 * platform diagnostic endpoint; tenants get a graceful no-op (the
 * /platform/* endpoint 403s harmlessly) so the click does nothing
 * visible-and-broken.
 */
export function EngineBadge() {
  const [status, setStatus] = useState({ alive: null });
  const [debug, setDebug] = useState(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    let alive = true;
    const poll = () => {
      api
        .get("/engine/status")
        .then((r) => alive && setStatus(r.data))
        .catch(() => alive && setStatus((prev) => ({ ...prev, alive: false })));
    };
    poll();
    const iv = setInterval(poll, 5000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, []);
  const aliveState = useEngineAliveDebounced(status.alive, 3, status);
  const color =
    aliveState === "up"
      ? "bg-[hsl(var(--ok))]/15 text-[hsl(var(--ok))] border-[hsl(var(--ok))]/30"
      : aliveState === "down"
      ? "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30"
      : "bg-[hsl(var(--warn))]/10 text-[hsl(var(--warn))] border-[hsl(var(--warn))]/30";
  const label =
    aliveState === "up"
      ? t("dashboard.engine.up", "Engine UP")
      : aliveState === "down"
      ? t("dashboard.engine.down", "Engine DOWN")
      : t("dashboard.engine.checking", "Checking…");
  const dotColor =
    aliveState === "up"
      ? "bg-[hsl(var(--ok))] pulse-live"
      : aliveState === "down"
      ? "bg-[hsl(var(--danger))]"
      : "bg-[hsl(var(--warn))]";
  const interactive = aliveState !== "up";
  const onDiagnose = async (e) => {
    e?.stopPropagation?.();
    if (!interactive) return;
    setDebugOpen((open) => !open);
    if (!debug && !debugLoading) {
      setDebugLoading(true);
      try {
        const r = await api.get("/platform/engine/probe-debug");
        setDebug(r.data);
      } catch (err) {
        setDebug({ error: err?.response?.data?.detail || String(err) });
      } finally {
        setDebugLoading(false);
      }
    }
  };
  return (
    <div className="relative">
      <div
        data-testid="engine-status-badge"
        data-engine-state={aliveState}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : -1}
        onClick={interactive ? onDiagnose : undefined}
        onKeyDown={
          interactive
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") onDiagnose(e);
              }
            : undefined
        }
        title={
          interactive
            ? t("dashboard.engine.diagnoseHint", "Click to diagnose")
            : undefined
        }
        className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1 text-xs font-medium ${color} ${
          interactive
            ? "cursor-pointer hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
            : ""
        }`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} />
        {label}
        {aliveState === "up" && status.host && (
          <span className="hidden xl:inline text-muted-foreground font-mono">
            {status.host}:{status.port}
          </span>
        )}
      </div>
      {debugOpen && interactive && (
        <div
          data-testid="engine-debug-panel"
          className="absolute right-0 top-full mt-2 w-[420px] max-w-[95vw] z-50 rounded-md border border-border bg-popover text-popover-foreground shadow-lg p-3 text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">
              {t("dashboard.engine.diagnoseTitle", "Engine probe diagnostics")}
            </div>
            <button
              type="button"
              onClick={() => setDebugOpen(false)}
              className="text-muted-foreground hover:text-foreground"
              data-testid="engine-debug-close"
              aria-label="Close diagnostics"
            >
              ×
            </button>
          </div>
          {debugLoading ? (
            <div className="text-muted-foreground">
              {t("common.loading", "Loading…")}
            </div>
          ) : debug?.error ? (
            <div className="text-[hsl(var(--danger))]">{debug.error}</div>
          ) : debug ? (
            <div className="space-y-2">
              <div>
                <span className="font-medium">
                  {t("dashboard.engine.target", "Target")}:{" "}
                </span>
                <span className="font-mono">
                  {debug.summary?.target_host}:{debug.summary?.target_port}
                </span>
              </div>
              <div>
                <span className="font-medium">
                  {t("dashboard.engine.attempts", "Attempts")}:{" "}
                </span>
                {(debug.attempts || []).map((a) => (
                  <span key={a.attempt} className="mr-2 font-mono">
                    #{a.attempt} {a.ok ? "✓" : "✗"} {a.elapsed_ms}ms
                  </span>
                ))}
              </div>
              <div>
                <span className="font-medium">
                  {t("dashboard.engine.envVars", "Env")}:{" "}
                </span>
                <span className="font-mono break-all">
                  HOST={debug.env?.ELSWITCH_HOST}, PORT=
                  {debug.env?.ELSWITCH_PORT}, TIMEOUT=
                  {debug.env?.ELSWITCH_PROBE_TIMEOUT}
                </span>
              </div>
              <div>
                <span className="font-medium">
                  {t("dashboard.engine.listenPorts", "Bound ports")}:{" "}
                </span>
                {(debug.listen_ports?.all_bound || []).map((lp, i) => (
                  <span key={i} className="mr-2 font-mono">
                    :{lp.port}/{lp.transport}
                    {lp.is_primary ? " (primary)" : ""}
                  </span>
                ))}
              </div>
              {debug.hint && (
                <div className="mt-2 p-2 rounded bg-[hsl(var(--muted))] text-[11px] leading-relaxed">
                  💡 {debug.hint}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
